import saveAs from 'file-saver';
import { normalize, denormalize } from 'normalizr';
import { combineEpics, ofType } from 'redux-observable';
import {
  switchMap, catchError, mergeMap, map,
} from 'rxjs/operators';
import api from 'api/index';
import { handleError } from 'base/errors/store/actions';
import { createUserSuccess } from 'auth/store/users/actions';
import { closeModal } from 'modal/store/actions';
import * as actionTypes from './actionTypes';
import { contactSchema, contactsSchema } from '../../store/schema';
import {
  createContactSuccess,
  deleteContactSuccess,
  downloadVcardSuccess,
  fetchAllContactsBySpaceIdSuccess,
  fetchAllContactsSuccess,
  fetchMyContactSuccess,
  fetchContactByUserIdSuccess,
  updateContactSuccess, importContactsSuccess,
} from './actions';
import { entitiesFetchSuccess } from '../../store/entities/actions';
import { getEntities } from '../../store/entities/selectors';


const fetchAll = (action$) => action$.pipe(
  ofType(actionTypes.FETCH_ALL),
  switchMap(() => api.contacts.fetchAll().pipe(
    map((response) => normalize(response.body, contactsSchema)),
    mergeMap(({ entities, result }) => [
      entitiesFetchSuccess(entities),
      fetchAllContactsSuccess(result),
    ]),
    catchError((error) => [handleError(error)]),
  )),
);


const fetchAllBySpaceId = (action$) => action$.pipe(
  ofType(actionTypes.FETCH_ALL_BY_SPACE_ID),
  switchMap((action) => api.contacts.fetchAllBySpaceId(action.spaceId).pipe(
    map((response) => normalize(response.body, contactsSchema)),
    mergeMap(({ entities, result }) => [
      entitiesFetchSuccess(entities),
      fetchAllContactsBySpaceIdSuccess(result, action.spaceId),
    ]),
    catchError((error) => [handleError(error)]),
  )),
);


const fetchOneByUserId = (action$) => action$.pipe(
  ofType(actionTypes.FETCH_BY_USER_ID),
  switchMap((action) => api.contacts.fetchOneByUserId(action.userId).pipe(
    map((response) => normalize(response.body, contactSchema)),
    mergeMap(({ entities, result }) => [
      entitiesFetchSuccess(entities),
      fetchContactByUserIdSuccess(result),
    ]),
    // Not every user has a contact -> only treat non 404 as error
    catchError((error) => ((error.status && [404].includes(error.status))
      ? [fetchContactByUserIdSuccess(null)]
      : [handleError(error)])),
  )),
);


const downloadVcardEpic = (action$) => action$.pipe(
  ofType(actionTypes.DOWNLOAD_VCARD),
  mergeMap((action) => api.contacts.downloadVcard(action.contact).pipe(
    map((response) => {
      saveAs(response.body, `${action.contact.lastName}.vcf`);
      return downloadVcardSuccess(response.text, action.contact);
    }),
    catchError((error) => [handleError(error)]),
  )),
);


const createContact = (action$, state$) => action$.pipe(
  ofType(actionTypes.CREATE),
  switchMap((action) => {
    const contact = denormalize(action.contact, contactSchema, getEntities(state$.value));
    return api.contacts.create(contact).pipe(
      mergeMap((response) => {
        const { entities } = normalize(response.body, contactSchema);
        const nextActions = [
          entitiesFetchSuccess(entities),
          createContactSuccess(response.body),
        ];
        if (response.body && response.body.user && response.body.user.id) {
          nextActions.push(createUserSuccess(response.body.user));
        }
        return nextActions;
      }),
      catchError((error) => [handleError(error)]),
    );
  }),
);


const updateContact = (action$, state$) => action$.pipe(
  ofType(actionTypes.UPDATE),
  switchMap((action) => {
    const contact = denormalize(action.contact, contactSchema, getEntities(state$.value));
    return api.contacts.update(contact).pipe(
      mergeMap((response) => {
        const { entities } = normalize(response.body, contactSchema);
        return [
          entitiesFetchSuccess(entities),
          updateContactSuccess(response.body),
        ];
      }),
      catchError((error) => [handleError(error)]),
    );
  }),
);


const deleteContact = (action$) => action$.pipe(
  ofType(actionTypes.DELETE),
  switchMap((action) => api.contacts.delete(action.contact).pipe(
    mergeMap(() => [
      deleteContactSuccess(action.contact.id),
    ]),
    catchError((error) => [handleError(error)]),
  )),
);


const importContacts = (action$) => action$.pipe(
  ofType(actionTypes.IMPORT),
  switchMap((action) => api.contacts.importFromCsv(action.csv).pipe(
    mergeMap((response) => {
      const { entities, result } = normalize(response.body, contactsSchema);
      const nextActions = [
        entitiesFetchSuccess(entities),
        importContactsSuccess(result),
        closeModal('CONTACT_IMPORT'),
      ];
      response.body.forEach((contact) => {
        nextActions.push(createUserSuccess(contact.user));
      });
      return nextActions;
    }),
    catchError((error) => [handleError(error)]),
  )),

);


const fetchMyContact = (action$) => action$.pipe(
  ofType(actionTypes.FETCH_ME),
  switchMap(() => api.contacts.fetchMyContact().pipe(
    map((response) => normalize(response.body, contactSchema)),
    mergeMap(({ entities, result }) => [
      entitiesFetchSuccess(entities),
      fetchMyContactSuccess(result),
    ]),
    // Not every user has a contact -> only treat non 404 as error
    catchError((error) => ((error.status && [404].includes(error.status))
      ? [fetchMyContactSuccess(null)]
      : [handleError(error)])),
  )),

);


const updateMyContact = (action$) => action$.pipe(
  ofType(actionTypes.UPDATE_ME),
  switchMap((action) => api.contacts.updateMyContact(action.contact).pipe(
    mergeMap((response) => {
      const { entities } = normalize(response.body, contactSchema);
      return [
        entitiesFetchSuccess(entities),
        updateContactSuccess(action.contact.id),
      ];
    }),
    catchError((error) => [handleError(error)]),
  )),
);

export default combineEpics(
  fetchAll,
  fetchAllBySpaceId,
  fetchOneByUserId,
  downloadVcardEpic,
  createContact,
  updateContact,
  deleteContact,
  importContacts,
  fetchMyContact,
  updateMyContact,
);
